// 用户状态过滤器
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import type { IFilterParams, IFilterComp } from "ag-grid-community";
import { useTranslation } from "@/locales";

// 函数组件包装器，用于正确使用 Hook
const UserStateFilterWrapper = forwardRef<IFilterComp, { params: IFilterParams }>((props, ref) => {
  const { t } = useTranslation();
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [eGui, setEGui] = useState<HTMLElement | null>(null);

  const stateOptions = [
    { value: "OK", label: t("admin.userStatus.active") },
    { value: "INACTIVE", label: t("admin.userStatus.inactive") },
    { value: "SUSPENDED", label: t("admin.userStatus.banned") },
  ];

  useEffect(() => {
    const gui = document.createElement("div");
    gui.innerHTML = `
      <div style="padding: 8px; min-width: 100px; max-height: 300px; overflow-y: auto;">
        <div style="margin-bottom: 8px;">
          <label style="display: flex; align-items: center; cursor: pointer;">
            <input type="radio" name="userstate-filter" value="" style="margin-right: 8px;" checked />
            <span>${t("common.all")}</span>
          </label>
        </div>
        <div style="border-top: 1px solid #e0e0e0; padding-top: 8px;">
          ${stateOptions
            .map(
              (option) => `
              <div style="margin-bottom: 6px;">
                <label style="display: flex; align-items: center; cursor: pointer;">
                  <input type="radio" name="userstate-filter" value="${option.value}" style="margin-right: 8px;" />
                  <span>${option.label}</span>
                </label>
              </div>
            `
            )
            .join("")}
        </div>
      </div>
    `;

    // 添加事件监听
    const radioButtons = gui.querySelectorAll('input[name="userstate-filter"]');
    radioButtons.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const target = e.target as HTMLInputElement;
        if (target.checked) {
          const newState = target.value || null;
          setSelectedState(newState);
          
          // 触发父组件刷新
          if (props.params && props.params.filterChangedCallback) {
            props.params.filterChangedCallback();
          }
        }
      });
    });

    setEGui(gui);
  }, [t, stateOptions, props.params]);

  useImperativeHandle(ref, () => ({
    init: (params: IFilterParams) => {
      // 初始化已在 useEffect 中完成
    },
    
    getGui: () => eGui,
    
    isFilterActive: () => selectedState !== null,
    
    doesFilterPass: (params: any) => {
      if (!selectedState) return true;
      return params.data.state === selectedState;
    },
    
    getModel: () => selectedState ? { state: selectedState } : null,
    
    setModel: (model: any) => {
      const newState = model ? model.state : null;
      setSelectedState(newState);
      
      if (eGui) {
        const radioButtons = eGui.querySelectorAll('input[name="userstate-filter"]');
        radioButtons.forEach((radio) => {
          const input = radio as HTMLInputElement;
          input.checked = input.value === (newState || "");
        });
      }
    },
    
    destroy: () => {
      // 清理工作
    }
  }));

  return null; // 这个组件不渲染任何内容，只是用来管理 ag-grid 过滤器
});

UserStateFilterWrapper.displayName = "UserStateFilterWrapper";

// 创建类组件适配器
export class UserStateFilterComponent implements IFilterComp {
  private wrapperRef: React.RefObject<IFilterComp>;
  private reactElement: React.ReactElement;
  private container: HTMLElement;

  constructor() {
    this.wrapperRef = React.createRef();
    this.container = document.createElement("div");
  }

  init(params: IFilterParams): void {
    this.reactElement = React.createElement(UserStateFilterWrapper, {
      ref: this.wrapperRef,
      params: params
    });

    // 使用 React 18 的 createRoot API
    import("react-dom/client").then(({ createRoot }) => {
      const root = createRoot(this.container);
      root.render(this.reactElement);
    });
  }

  getGui(): HTMLElement {
    return this.wrapperRef.current?.getGui() || this.container;
  }

  isFilterActive(): boolean {
    return this.wrapperRef.current?.isFilterActive() || false;
  }

  doesFilterPass(params: any): boolean {
    return this.wrapperRef.current?.doesFilterPass(params) || true;
  }

  getModel(): any {
    return this.wrapperRef.current?.getModel() || null;
  }

  setModel(model: any): void {
    this.wrapperRef.current?.setModel(model);
  }

  destroy(): void {
    this.wrapperRef.current?.destroy();
  }
}

export default UserStateFilterComponent;
